import React, { FC, useCallback } from 'react';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { useFormContext, useWatch } from 'react-hook-form';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from '@buyside/components/presentation/Button/button';
import InputField from '@buyside/components/presentation/Fields/inputField';
import SelectField from '@buyside/components/presentation/Fields/selectField';
import UserCreditDataFields from '@buyside/components/presentation/IdeaForm/Positions/PositionFields/userCreditDataField';

import {
  determineIsSubunitCurrency,
  determineParentCurrencyCode,
  determineParentCurrencyName,
  IdeaFormInput,
  userCreditDataDefaultValues
} from '@buyside/util/researchSubmission';

const { currencyCodes, assetClasses } = require('buyside/util/constantsFromRails.json');

import styles from '../positionsStyles.scss';

const UserDefinedAssetField: FC<{
  currencyCode: string;
  index: number;
}> = ({ currencyCode, index }) => {
  const { control, setValue } = useFormContext<IdeaFormInput>();

  const [priceTarget, userCreditData] = useWatch({
    control,
    name: [`positions.${index}.priceTarget`, `positions.${index}.userDefinedAsset.userCreditData`]
  });

  const assetClassOnChangeCallback = useCallback(
    (assetClassId: string) => {
      const selectedAssetClassName = assetClasses.find(
        (assetClass: { id: number; name: string }) => assetClass.id === Number(assetClassId)
      )?.name;

      if (selectedAssetClassName === 'Corporate Debt') {
        setValue(
          `positions.${index}.userDefinedAsset.userCreditData`,
          userCreditDataDefaultValues,
          {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          }
        );
      } else {
        setValue(`positions.${index}.userDefinedAsset.userCreditData`, null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    },
    [index, setValue]
  );

  const recentPriceOnChangeCallback = useCallback(
    (recentPrice: string) => {
      if (!recentPrice || !priceTarget) return;

      const direction = priceTarget ? (priceTarget > Number(recentPrice) ? 'long' : 'short') : '';

      return setValue(`positions.${index}.direction`, direction, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    },
    [index, priceTarget, setValue]
  );

  const generateSubunitCurrencyHelperElement = useCallback(
    (priceTarget: string) => {
      if (priceTarget && determineIsSubunitCurrency(currencyCode))
        return (
          <label className={styles.positionAssetSubunitCurrencyLabel}>
            ({(Number(priceTarget) * 100).toFixed(2)} {currencyCode})
          </label>
        );

      return null;
    },
    [currencyCode]
  );

  return (
    <>
      <div className={styles.positionRow}>
        <SelectField
          label="Asset Class"
          name={`positions.${index}.userDefinedAsset.assetClassId`}
          options={assetClasses.map((assetClass: { id: number; name: string }) => ({
            label: assetClass.name,
            value: assetClass.id
          }))}
          onChangeCallback={assetClassOnChangeCallback}
        />
      </div>
      <div className={styles.positionRow}>
        <InputField
          label="Symbol"
          name={`positions.${index}.userDefinedAsset.symbol`}
          type="text"
        />
        <InputField
          label="Entity Name"
          name={`positions.${index}.userDefinedAsset.entityName`}
          type="text"
        />
      </div>
      <div className={styles.positionRow}>
        <InputField
          endAdornment={determineParentCurrencyCode(currencyCode)}
          generateHelperElement={generateSubunitCurrencyHelperElement}
          label="Recent Price"
          name={`positions.${index}.userDefinedAsset.recentPrice`}
          onChangeCallback={recentPriceOnChangeCallback}
          type="number"
        />
        <SelectField
          label="Currency"
          isSearchable
          name={`positions.${index}.userDefinedAsset.currencyId`}
          options={currencyCodes.map((currency: { id: number; code: string; name: string }) => {
            const parentCurrencyCode = determineParentCurrencyCode(currency.code);
            const parentCurrencyName = determineParentCurrencyName(currency.name);

            return { label: `${parentCurrencyCode} - ${parentCurrencyName}`, value: currency.id };
          })}
        />
      </div>
      {userCreditData ? <UserCreditDataFields index={index} /> : null}
      <Button
        faIcon={faSearch}
        onClick={() => {
          setValue(`positions.${index}.userDefinedAsset`, null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
          setValue(`positions.${index}.direction`, '', {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
          setValue(`positions.${index}.priceTarget`, null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        }}
        size={BUTTON_SIZES.SMALL}
        text="Search For A Security Instead"
        type={BUTTON_TYPES.SECONDARY}
        width={BUTTON_WIDTHS.FULL}
      />
    </>
  );
};

export default UserDefinedAssetField;
